/**
 * 通用常量和类型定义
 *
 * @author YSFL Team
 * @date 2025-10-16
 */

/**
 * 验证规则接口
 *
 * 用于定义表单字段的验证规则
 * 
 * ⚠️ 这是全局统一的验证规则接口，各模块应从此处导入
 */
export interface ValidationRule {
  /** 是否必填 */
  required?: boolean
  /** 最小长度 */
  minLength?: number
  /** 最大长度 */
  maxLength?: number
  /** 最小值 */
  min?: number
  /** 最大值 */
  max?: number
  /** 正则表达式 */
  pattern?: RegExp
  /** 枚举值（允许的值列表） */
  enum?: readonly string[]
  /** 小数位数 */
  precision?: number
  /** 错误提示消息 */
  message?: string
  /** 自定义验证函数 */
  validator?: (value: any) => boolean | Promise<boolean>
}

/**
 * 通用正则表达式
 */
export const COMMON_REGEX = {
  /** 手机号（中国） */
  MOBILE: /^1[3-9]\d{9}$/,
  /** 邮箱 */
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  /** 身份证号 */
  ID_CARD: /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/,
  /** URL */
  URL: /^https?:\/\/.+$/,
  /** IP地址 */
  IP: /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/,
  /** 邮政编码（中国） */
  POSTAL_CODE: /^\d{6}$/,
  /** 大写字母、数字、横线 */
  CODE: /^[A-Z0-9-]+$/,
  /** 用户名（字母、数字、下划线） */
  USERNAME: /^[a-zA-Z0-9_]{4,20}$/,
  /** 中文姓名 */
  CHINESE_NAME: /^[\u4e00-\u9fa5]{2,4}$/,
} as const

/**
 * 默认国家常量
 */
export const DEFAULT_COUNTRY = '中国' as const;

/**
 * 通用验证规则
 */
export const COMMON_VALIDATION_RULES = {
  /** 必填规则 */
  required: {
    required: true,
    message: '此字段为必填项',
  },

  /** 手机号规则 */
  phone: {
    required: false,
    pattern: COMMON_REGEX.MOBILE,
    message: '请输入正确的手机号',
  },

  /** 邮箱规则 */
  email: {
    required: false,
    pattern: COMMON_REGEX.EMAIL,
    message: '请输入正确的邮箱地址',
  },

  /** 身份证号规则 */
  idCard: {
    required: false,
    pattern: COMMON_REGEX.ID_CARD,
    message: '请输入正确的身份证号',
  },

  /** URL规则 */
  url: {
    required: false,
    pattern: COMMON_REGEX.URL,
    message: '请输入正确的URL地址',
  },

  /** 金额规则 */
  amount: {
    required: false,
    min: 0,
    max: 999999999.99,
    precision: 2,
    message: '请输入正确的金额（最多2位小数）',
  },

  /** 正整数规则 */
  positiveInteger: {
    required: false,
    min: 1,
    pattern: /^\d+$/,
    message: '请输入正整数',
  },

  /** 非负整数规则 */
  nonNegativeInteger: {
    required: false,
    min: 0,
    pattern: /^\d+$/,
    message: '请输入非负整数',
  },

  /** 百分比规则（0-100） */
  percentage: {
    required: false,
    min: 0,
    max: 100,
    precision: 2,
    message: '请输入0-100之间的数值',
  },
} as const

